import { getCookie } from '@/core/utils/cookieUtils';
import { getUserInfo, getPageName } from '@/shared/features/analytics/utils/commonAttributes';

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
  }
}

/**
 * Gets the current page type classification.
 */
function getCurrentPageType(): string {
  const pageNameAttr = getPageName();
  return pageNameAttr['Page Name'] || '';
}

/** 
 * Gets common attributes for Facebook Pixel events to parity with legacy tracking.
 * Reads real customer details from the user context cache.
 *
 * @param customAttributes - Specific event properties to merge with common attributes.
 * @returns An object containing common Facebook attributes merged with custom attributes.
 */

export function getFacebookCommonAttributes(
  customAttributes?: Record<string, unknown>
): Record<string, unknown> {
  if (typeof window === 'undefined') {
    return { ...customAttributes };
  }

  const userInfo = getUserInfo();
  const isLoggedIn = userInfo?.isLoggedIn === true;

  const external_id = isLoggedIn
    ? String(userInfo?.userCode || '')
    : null;
  const em = isLoggedIn && typeof userInfo?.email === 'string' ? userInfo.email : null;
  const ph = isLoggedIn && typeof userInfo?.phoneNumber === 'string' ? userInfo.phoneNumber : null;

  // Confirmed profile details
  const fn = isLoggedIn && typeof userInfo?.customerName === 'string' ? userInfo.customerName : null;
  const ln = null;

  const ct = isLoggedIn && typeof userInfo?.cityName === 'string' ? userInfo.cityName : null;
  const st = isLoggedIn && typeof userInfo?.state === 'string' ? userInfo.state : null;
  const zp = userInfo?.pincode ? String(userInfo.pincode) : getCookie('pincode') || null;
  const country = isLoggedIn && typeof userInfo?.country === 'string' ? userInfo.country : null;

  
  const commonAttributes: Record<string, unknown> = {
    event_time: Math.floor(Date.now() / 1000),
    event_id: null,
    action_source: 'website',
    event_source_url: window.location.href,
    
    // Facebook identifiers
    fbp: getCookie('_fbp'),
    fbc: getCookie('_fbc'),
    
    // Client information
    client_ip_address: null, // cannot read customer IP
    client_user_agent: navigator.userAgent,
    
    // Customer identifiers
    external_id,
    em,
    ph,
    fn,
    ln,
    ct,
    st,
    zp,
    country,
    currency: 'INR',
    
    // Custom params
    source_surface: 'website',
    producer_system: 'web_pixel',
    surface_identifier: window.location.pathname,
    surface_title: document.title,
    surface_type: getCurrentPageType(),
  };

  return {
    ...commonAttributes,
    ...customAttributes,
  };
}

/**
 * Centralized Meta/Facebook Pixel Event Tracker
 *
 * Logs the event parameters to the console and invokes the global fbq function.
 *
 * @param eventName - The standard or custom Facebook event name (e.g. AddToWishlist, Search).
 * @param customAttributes - Optional attributes to include in the tracking payload.
 */
export function trackFacebookEvent(
  eventName: string,
  customAttributes?: Record<string, unknown>
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const rawPayload = getFacebookCommonAttributes(customAttributes);

  // Strip null and undefined attributes
  const payload: Record<string, unknown> = {};
  for (const key of Object.keys(rawPayload)) {
    const val = rawPayload[key];
    if (val !== null && val !== undefined) {
      payload[key] = val;
    }
  }

  console.log(`Facebook Pixel Event '${eventName}' triggered:`, payload);

  if (typeof window.fbq === 'function') {
    try {
      window.fbq('track', eventName, payload);
    } catch (error) {
      console.error(`Error tracking Facebook Pixel Event '${eventName}':`, error);
    }
  } else {
    console.warn(`Facebook Pixel fbq is not available on window. Event '${eventName}' was skipped.`);
  }
}
